/**
 * Browser download helpers.
 *
 * Both of these were reimplemented in four places (the review CSV export, the
 * commented-Word export, the summary export and the ticket-modal attachment
 * download), each with slightly different lifetime handling for the object URL.
 * One copy, one lifetime.
 */

/**
 * Hand a blob to the browser as a file save.
 *
 * The anchor is appended to the document before clicking because Firefox
 * ignores a click on a detached node, and the object URL is revoked on the next
 * tick rather than immediately — Safari cancels an in-flight download when the
 * URL is revoked synchronously.
 */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Read the server's own file name out of a `Content-Disposition` header.
 *
 * Prefers the RFC 5987 `filename*=UTF-8''…` form so non-ASCII names survive,
 * and falls back to the plain `filename="…"` form. Returns null when the header
 * is absent or carries neither — callers supply their own fallback name.
 */
export function filenameFromContentDisposition(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null;

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const plainMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return plainMatch?.[1] ?? null;
}
